import React from 'react';
import { BarChart3, Activity, Star, Target, AlertTriangle, Wifi, CheckCircle, TrendingUp } from 'lucide-react';
import { styles } from '../../styles/globalStyles';
import { dashboardStyles } from '../../styles/dashboardStyles';
import { dashboardData } from '../../data/dashboardData';
import { getAlertColor } from '../../data/constants';

const DashboardSection = () => {
  return (
    <section id="dashboard" style={{ ...styles.section, ...dashboardStyles.dashboardSection }}>
      <div style={styles.sectionContent}>
        <h2 style={styles.sectionTitle}>
          Real-time 
          <span style={{ background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}> Operations Dashboard</span>
        </h2>
        <p style={styles.sectionSubtitle}>
          Monitor your AI agents performance, usage patterns, and system health in real-time
        </p>

        {/* Overview Cards */}
        <div style={dashboardStyles.dashboardGrid}>
          {/* System Overview */}
          <div style={dashboardStyles.dashboardCard}>
            <h3 style={dashboardStyles.dashboardCardTitle}>
              <BarChart3 size={18} />
              System Overview
            </h3>
            <div style={dashboardStyles.overviewGrid}>
              <div style={dashboardStyles.overviewItem}>
                <div style={dashboardStyles.overviewValue}>{dashboardData.overview.totalAgents}</div>
                <div style={dashboardStyles.overviewLabel}>Total Agents</div>
              </div>
              <div style={dashboardStyles.overviewItem}>
                <div style={dashboardStyles.overviewValue}>{dashboardData.overview.activeAgents}</div>
                <div style={dashboardStyles.overviewLabel}>Active</div>
              </div>
              <div style={dashboardStyles.overviewItem}>
                <div style={dashboardStyles.overviewValue}>{(dashboardData.overview.totalTokens / 1000000).toFixed(1)}M</div>
                <div style={dashboardStyles.overviewLabel}>Total Tokens</div>
              </div>
              <div style={dashboardStyles.overviewItem}>
                <div style={dashboardStyles.overviewValue}>{dashboardData.overview.uptime}%</div>
                <div style={dashboardStyles.overviewLabel}>Uptime</div>
              </div>
              <div style={dashboardStyles.overviewItem}>
                <div style={dashboardStyles.overviewValue}>{dashboardData.overview.dailyRequests}</div>
                <div style={dashboardStyles.overviewLabel}>Daily Requests</div>
              </div>
              <div style={dashboardStyles.overviewItem}>
                <div style={dashboardStyles.overviewValue}>{dashboardData.overview.avgResponseTime}s</div>
                <div style={dashboardStyles.overviewLabel}>Avg Response</div>
              </div>
            </div>
          </div>

          {/* Real-time Metrics */}
          <div style={dashboardStyles.dashboardCard}>
            <h3 style={dashboardStyles.dashboardCardTitle}>
              <Activity size={18} />
              Real-time Metrics
            </h3>
            <div style={dashboardStyles.realtimeGrid}>
              {dashboardData.realtimeStats.map((stat, index) => (
                <div key={index} style={dashboardStyles.realtimeItem}>
                  <div style={{ ...dashboardStyles.realtimeValue, color: stat.color }}>{stat.value}</div>
                  <div style={dashboardStyles.realtimeLabel}>{stat.label}</div>
                  <div style={{ ...dashboardStyles.realtimeTrend, color: stat.trend.startsWith('+') ? '#10b981' : stat.trend.startsWith('-') ? '#ef4444' : '#8b5cf6' }}>
                    <TrendingUp size={12} />
                    {stat.trend}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div style={dashboardStyles.dashboardGrid}>
          {/* Top Performing Agents */}
          <div style={dashboardStyles.dashboardCard}>
            <h3 style={dashboardStyles.dashboardCardTitle}>
              <Star size={18} />
              Top Performing Agents
            </h3>
            <div style={dashboardStyles.agentList}>
              {dashboardData.topAgents.map((agent, index) => (
                <div key={index} style={dashboardStyles.agentListItem}>
                  <div style={dashboardStyles.agentInfo}>
                    <div style={dashboardStyles.agentName}>{agent.name}</div>
                    <div style={dashboardStyles.agentStats}>{agent.requests} requests today</div>
                  </div>
                  <div style={dashboardStyles.usageBar}>
                    <div style={{ ...dashboardStyles.usageProgress, width: `${agent.usage}%` }}></div>
                  </div>
                  <div style={{
                    ...dashboardStyles.statusBadge,
                    background: agent.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                    color: agent.status === 'active' ? '#10b981' : '#f59e0b'
                  }}>
                    {agent.status}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Usage by Category */}
          <div style={dashboardStyles.dashboardCard}>
            <h3 style={dashboardStyles.dashboardCardTitle}>
              <Target size={18} />
              Usage by Category
            </h3>
            <div style={dashboardStyles.categoryChart}>
              {dashboardData.usageByCategory.map((category, index) => (
                <div key={index} style={dashboardStyles.categoryItem}>
                  <div style={{ minWidth: '80px', fontSize: '14px', fontWeight: '500' }}>
                    {category.category}
                  </div>
                  <div style={dashboardStyles.categoryBar}>
                    <div style={{ ...dashboardStyles.categoryProgress, width: `${category.percentage}%` }}>
                      <span style={dashboardStyles.categoryLabel}>{category.percentage}%</span>
                    </div>
                  </div>
                  <div style={dashboardStyles.categoryPercentage}>
                    {category.requests}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* System Alerts */}
          <div style={dashboardStyles.dashboardCard}>
            <h3 style={dashboardStyles.dashboardCardTitle}>
              <AlertTriangle size={18} />
              Recent Alerts
            </h3>
            <div style={dashboardStyles.alertsList}>
              {dashboardData.alerts.map((alert, index) => (
                <div key={index} style={dashboardStyles.alertItem}>
                  {alert.type === 'warning' && <AlertTriangle size={16} style={{ color: getAlertColor(alert.type) }} />}
                  {alert.type === 'info' && <Wifi size={16} style={{ color: getAlertColor(alert.type) }} />}
                  {alert.type === 'success' && <CheckCircle size={16} style={{ color: getAlertColor(alert.type) }} />}
                  <div style={dashboardStyles.alertContent}>
                    <div style={dashboardStyles.alertMessage}>{alert.message}</div>
                    <div style={dashboardStyles.alertTime}>{alert.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardSection;